import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { FORM_FIELDS, FORM_NAME, REQUEST_ERROR_MESSAGE } from './constants';
import { transformToValidationErrors } from './utils';
import { DISMISS_TIME } from '../../api/constants';
import {
	CREATE_CAR,
	DELETE_CAR,
	GET_CAR_INFO,
	GET_LEGALS,
	GET_CAR_TYPES,
	GET_EVENT_DATA,
	GET_MODES,
	GET_OWNERSHIPS,
	GET_TRAILERS_LIST,
	setCarInfoAction,
	setLegalsAction,
	setIsLegalsLoadingAction,
	setCarTypesAction,
	setEventDataAction,
	setIsCarDeletingAction,
	setIsCarInfoLoadingAction,
	setIsCarSavingAction,
	setIsCarTypesLoadingAction,
	setIsEventDataLoadingAction,
	setIsModesLoadingAction,
	setIsOwnershipsLoadingAction,
	setIsTrailersListLoadingAction,
	setModesAction,
	setOwnershipsAction,
	setTrailersListAction,
	UPDATE_CAR,
} from './actions';
import {
	createCarRequest,
	deleteCarRequest,
	getCarEventRequest,
	getCarInfoRequest,
	getLegalsDictionaryRequest,
	getCarTypesRequest,
	getModesDictionaryRequest,
	getOwnershipsDictionaryRequest,
	getTrailersListRequest,
	updateCarRequest,
} from '../../api/requests';
import { getDataForCreateOrUpdateCarSelector, getFormIsInvalidSelector } from './selectors';
import { getCarsListSelector, getRawPaginationSelector } from '../cars/selectors';
import { setCarsListAction, setPaginationAction, getCarsListAction } from '../cars/actions';
import { errorHandler } from '../../api/utils';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { getTransportsListAction } from '../transports/actions';

export function* getCarInfoSaga({ id }) {
	try {
		yield put(setIsCarInfoLoadingAction(true));

		const { data, message, toast } = yield call(getCarInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCarInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarInfoLoadingAction(false));
	}
}

export function* deleteCarSaga({ payload: { id, redirect, query } }) {
	try {
		yield put(setIsCarDeletingAction(true));

		const { data, message, toast } = yield call(deleteCarRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const cars = yield select(getCarsListSelector());
			yield put(setCarsListAction(cars.filter(({ id }) => id !== data.id)));

			const pagination = yield select(getRawPaginationSelector());
			if (pagination) {
				yield put(setPaginationAction({ ...pagination, total: pagination.total - 1 }));
			}

			redirect();
			yield put(getCarsListAction({ searchQuery: removeModalQuery(query.toString()), noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarDeletingAction(false));
	}
}

export function* createCarSaga({ payload: { redirect, resetState, searchQuery } }) {
	try {
		yield put(setIsCarSavingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateCarSelector());

		const { data, errors, message, toast } = yield call(createCarRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();
			resetState();
			yield put(getCarsListAction({ searchQuery: removeModalQuery(searchQuery.toString()), noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarSavingAction(false));
	}
}

export function* updateCarSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsCarSavingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateCarSelector());

		const { data, errors, message, toast } = yield call(updateCarRequest, id, dataForSave);
		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			const cars = yield select(getCarsListSelector());

			const updatedCar = {
				...data,
				type_name: data.type?.name ?? '---',
				link: `?${searchQuery ? `${searchQuery}&` : ''}modal=car&id=${data.id}`,
			};

			yield put(
				setCarsListAction(
					cars.map(car => {
						return car.id === data.id ? updatedCar : car;
					}),
				),
			);

			yield put(setCarInfoAction(data));

			redirect();

			if (pathname.includes('quality_block')) {
				const type = pathname.split('/')[2];
				yield put(
					getTransportsListAction({
						type,
						searchQuery: removeModalQuery(searchQuery.toString()),
						noLoading: true,
					}),
				);
			} else {
				yield put(
					getCarsListAction({ searchQuery: removeModalQuery(searchQuery.toString()), noLoading: true }),
				);
			}
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarSavingAction(false));
	}
}

export function* getTrailersListSaga() {
	try {
		yield put(setIsTrailersListLoadingAction(true));
		const { data, message, toast } = yield call(getTrailersListRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setTrailersListAction(
					data?.map(trailer => ({
						text: trailer.number,
						description: trailer.name,
						value: trailer.id,
						key: `${trailer.name.trim()}-${trailer.vin}-${trailer.id}`,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTrailersListLoadingAction(false));
	}
}

export function* getOwnershipsSaga() {
	try {
		yield put(setIsOwnershipsLoadingAction(true));

		const { data, message, toast } = yield call(getOwnershipsDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setOwnershipsAction([
					...data.map(ownership => ({
						key: ownership.id,
						value: ownership.id,
						text: ownership.name,
					})),
				]),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOwnershipsLoadingAction(false));
	}
}

export function* getLegalsSaga() {
	try {
		yield put(setIsLegalsLoadingAction(true));

		const { data, message, toast } = yield call(getLegalsDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setLegalsAction(
					data.map(legal => ({
						key: legal.id,
						value: legal.id,
						text: legal.name,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalsLoadingAction(false));
	}
}

export function* getTypesSaga() {
	try {
		yield put(setIsCarTypesLoadingAction(true));

		const { data, message, toast } = yield call(getCarTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setCarTypesAction(
					data.map(type => ({ key: String(type.id), value: String(type.id), text: type.name })),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarTypesLoadingAction(false));
	}
}

export function* getModesDataSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setModesAction(
					data
						.filter(mode => mode.id !== 1)
						.map(mode => ({ key: String(mode.id), value: String(mode.id), text: mode.name_with_symbol })),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getEventDataSaga({ payload: id }) {
	try {
		yield put(setIsEventDataLoadingAction(true));

		const { message, toast, reports } = yield call(getCarEventRequest, id);

		if (reports) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setEventDataAction(reports?.html || null));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsEventDataLoadingAction(false));
	}
}

export default function* carsModalSaga() {
	yield takeEvery(GET_CAR_INFO, getCarInfoSaga);
	yield takeEvery(DELETE_CAR, deleteCarSaga);
	yield takeEvery(CREATE_CAR, createCarSaga);
	yield takeEvery(UPDATE_CAR, updateCarSaga);
	yield takeEvery(GET_TRAILERS_LIST, getTrailersListSaga);
	yield takeEvery(GET_OWNERSHIPS, getOwnershipsSaga);
	yield takeEvery(GET_LEGALS, getLegalsSaga);
	yield takeEvery(GET_CAR_TYPES, getTypesSaga);
	yield takeEvery(GET_MODES, getModesDataSaga);
	yield takeEvery(GET_EVENT_DATA, getEventDataSaga);
}
